import React from 'react';

import { css } from '@emotion/react';
import { MICRO, SocialSecurityInputFormatter } from '@x1/util-format';
import currency from 'currency.js';

import HousingExpenseInput from './HousingExpenseInput';
import IncomeInput from './IncomeInput';
import AlertModal from 'src/components/creditApplication/AlertModal';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { useIsNfaStanford } from 'src/hooks/useAttribution';
import { useGlobalStore } from 'src/state/global';

const IncomePage = () => {
  const { nextPage } = React.useContext(NavigationContext);
  const storeSsn = useGlobalStore((s) => s.personalInformation.ssn);
  const ssnEditable = !storeSsn;
  const [income, setIncome] = React.useState<string>('');
  const [housingExpense, setHousingExpense] = React.useState<string>('');
  const [ssn, setSsn] = React.useState<string>('');
  const [showZeroIncomeAlert, setShowZeroIncomeAlert] = React.useState<boolean>(false);
  const isNfaStanford = useIsNfaStanford();

  const subtitle = ssnEditable ? (
    <span>
      We will use this to “soft” pull your credit report.
      <br />
      This will not affect your score.
    </span>
  ) : (
    'Please make sure your information is up to date.'
  );

  const buttonState = React.useMemo(() => {
    const ssnOk = !ssnEditable || SocialSecurityInputFormatter.deserialize(ssn).length === 9;
    const nonZeroIncome = !isNfaStanford ? income !== '$0' : true;
    const incomeOk = !!income && nonZeroIncome;
    const housingExpenseOk = !isNfaStanford ? housingExpense.trim() !== '' : true;
    return ssnOk && incomeOk && housingExpenseOk ? 'default' : 'disabled';
  }, [ssnEditable, ssn, income, housingExpense, isNfaStanford]);

  const onChangeSsn = React.useCallback(({ value }: { value: string }) => {
    const formatted = SocialSecurityInputFormatter.serialize(value);
    setSsn(formatted);
  }, []);

  const onDismissZeroIncomeAlert = React.useCallback(() => {
    setShowZeroIncomeAlert(false);
  }, []);

  const onContinue = React.useCallback(async () => {
    await nextPage();
    useGlobalStore.setState((s) => ({
      personalInformation: {
        ...s.personalInformation,
        ssn: ssnEditable ? ssn : s.personalInformation.ssn,
        incomeMicro: currency(income).value * MICRO,
        monthlyHousingExpenseMicro: Boolean(housingExpense) ? currency(housingExpense).value * MICRO : undefined,
      },
    }));
  }, [ssnEditable, ssn, income, housingExpense]);

  const onPrimaryContinueCta = React.useCallback(() => {
    if (income === '$0') {
      setShowZeroIncomeAlert(true);
      return;
    }
    onContinue();
  }, [income, onContinue]);

  return (
    <CreditApplicationPageLayout title='Your information' subtitle={subtitle}>
      <div css={root}>
        {ssnEditable && (
          <TextInput placeholder='Social Security Number' value={ssn} onChange={onChangeSsn} inputMode='numeric' />
        )}
        <IncomeInput value={income} onChange={setIncome} />
        {!isNfaStanford && <HousingExpenseInput value={housingExpense} onChange={setHousingExpense} />}
        <div css={buttonContainer}>
          <Button label='Continue' state={buttonState} width='full' type='submit' onClick={onPrimaryContinueCta} />
        </div>
      </div>
      <AlertModal
        title='Check your income is accurate'
        description={`Include money you earn or reasonably expect to earn, plus money that's regularly deposited into your account.`}
        visible={showZeroIncomeAlert}
        primaryButtonLabel='Update income'
        secondaryButtonLabel='Confirm $0 Income'
        onPrimaryButtonClick={onDismissZeroIncomeAlert}
        onSecondaryButtonClick={onContinue}
      />
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const buttonContainer = css`
  margin-top: 32px;
`;

export default IncomePage;
